import React, { useEffect, useState, useCallback } from "react";
import { Form, Input, Select, Checkbox, Descriptions, Collapse, Alert, Button, Row, Col, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchCountries } from "../../../redux/Country/actions";
import { fetchRoles } from "../../../redux/Roles/actions";
import { fetchCompanies } from "../../../redux/Company/actions"; 
import { fetchOverrides } from "../../../redux/Overrides/actions"; 
import AddRolesButton from "../Roles/AddRoleButton"; // Replace with actual import path
import AddOverridesButton from "../Overrides/AddOverrideButton"; // Replace with actual import path

const { Option } = Select;
const { Panel } = Collapse;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const UserForm = ({ form, initialValues, onFinish, showPassword }) => {
  const dispatch = useDispatch();
  const countries = useSelector((state) => state.countries?.countries || []);
  const roles = useSelector((state) => state.roles?.roles || []);
  const overrides = useSelector((state) => state.overrides?.overrides || []);
  const companies = useSelector((state) => state.companies?.companies || []);
  
  const [selectedRoles, setSelectedRoles] = useState(initialValues.roles || []);
  const [selectedOverrides, setSelectedOverrides] = useState(initialValues.overrides || []);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchCountries()),
          dispatch(fetchRoles()),
          dispatch(fetchCompanies()),
          dispatch(fetchOverrides())
        ]);
      } catch (err) {
        setError(err.message || "An error occurred");
      }
    };

    fetchData();
  }, [dispatch]);

  const handleRolesChange = useCallback((value) => {
    setSelectedRoles(value);
  }, []);

  const handleOverridesChange = useCallback((value) => {
    setSelectedOverrides(value);
  }, []);

  const handleSubmit = useCallback(async (values) => {
    try {
      await onFinish(values);
      setError(null);
    } catch (err) {
      setError(err.message || "An error occurred");
    }
  }, [onFinish]);

  const renderRoleDetails = (roleId) => {
    const role = roles.find((role) => role._id === roleId);
    if (!role) return null;

    return (
      <Collapse key={roleId}>

        <Panel header={`Details for ${role.name || "Role Details"}`} key={roleId}>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Capabilities">
              {role.defaultCapabilities && role.defaultCapabilities.length > 0 ? (
                role.defaultCapabilities.map((cap) => (
                  <Tag key={cap._id}>{cap.name}</Tag>
                ))
              ) : (
                "No capabilities"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Resources">
              {role.defaultRessources && role.defaultRessources.length > 0 ? (
                role.defaultRessources.map((res) => (
                  <Tag key={res._id}>{res.name}</Tag>
                ))
              ) : (
                "No resources"
              )}
            </Descriptions.Item>
          </Descriptions>
        </Panel>
      </Collapse>
    );
  };

  const renderOverrideDetails = (overrideId) => {
    const override = overrides.find((override) => override._id === overrideId);
    if (!override) return null;

    return (
      <Collapse key={overrideId}>
        <Panel header={`Details for ${override.name || "Override Details"}`} key={overrideId}>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Capabilities">
              {override.defaultCapabilities && override.defaultCapabilities.length > 0 ? (
                override.defaultCapabilities.map((cap) => (
                  <Tag key={cap._id}>{cap.name}</Tag>
                ))
              ) : (
                "No capabilities"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Resources">
              {override.defaultRessources && override.defaultRessources.length > 0 ? (
                override.defaultRessources.map((res) => (
                  <Tag key={res._id}>{res.name}</Tag>
                ))
              ) : (
                "No resources"
              )}
            </Descriptions.Item>
          </Descriptions>
        </Panel>
      </Collapse>
    );
  };

  return (
    <>
            {    console.log('initialValues',initialValues)
        }
      {error && <Alert message={error} type="error" showIcon />}
      <Form
        {...layout}
        name="userForm"
        form={form}
        onFinish={handleSubmit}
        initialValues={initialValues}
      >
        <Form.Item
          label="User name"
          name="username"
          rules={[{ required: true, message: "Please enter the username!" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[{ required: true, message: "Please enter the email!" }]}
        >
          <Input />
        </Form.Item>

        {showPassword && (
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please enter the password!" }]}
          >
            <Input.Password />
          </Form.Item>
        )}

        <Form.Item label="Phone" name="phone">
          <Input />
        </Form.Item>

        <Form.Item label="Address" name="address">
          <Input />
        </Form.Item>
        <Form.Item label="Roles">
          <Row gutter={8}>
            <Col flex="auto">
              <Form.Item name="roles" noStyle>
                <Select
                  mode="multiple"
                  placeholder="Select roles"
                  onChange={handleRolesChange}
                  value={selectedRoles}
                >
                  {roles.map((role) => (
                    <Option key={role._id} value={role._id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col flex="none">
              <AddRolesButton />
            </Col>
          </Row>
        </Form.Item>

        {selectedRoles.map(renderRoleDetails)}

        <Form.Item label="Overrides">
          <Row gutter={8}>
            <Col flex="auto">
              <Form.Item name="overrides" noStyle>
                <Select
                  mode="multiple"
                  placeholder="Select overrides"
                  onChange={handleOverridesChange}
                  value={selectedOverrides}
                >
                  {overrides.map((override) => (
                    <Option key={override._id} value={override._id}>
                      {override.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col flex="none">
              <AddOverridesButton />
            </Col>
          </Row>
        </Form.Item>

        {selectedOverrides.map(renderOverrideDetails)}


        <Form.Item label="Company" name="company">
          <Select placeholder="Select company">
            {companies.map((company) => (
              <Option key={company._id} value={company._id}>
                {company.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Country" name="country">
          <Select>
            {countries.map((country) => (
              <Option key={country._id} value={country._id}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Is Active" name="isActive" valuePropName="checked">
          <Checkbox />
        </Form.Item>


      </Form>
    </>
  );
};

export default UserForm;
